import { Box, Button, HStack, Text } from '@chakra-ui/react';
import { useNavigate } from 'react-router';

import { MaterialIcon } from '../../ui/MaterialIcon';
import { useMobileDetection } from '../../../hooks/ui/useMobileDetection';
import type { ChatMessage } from '../../../hooks/data/agentChatReducer';

interface BirdStatusLineProps {
  message: ChatMessage;
}

type StatusKind = NonNullable<ChatMessage['statusKind']>;

/**
 * Semantic-token tone and icon per kind. A `Record` over the kind, so a new
 * kind fails to compile here instead of quietly rendering as a success.
 */
const KIND_STYLE: Readonly<Record<StatusKind, { readonly tone: string; readonly icon: string }>> = {
  success: { tone: 'success', icon: 'task_alt' },
  error: { tone: 'error', icon: 'error' },
  // Calm, not an alarm: the researcher did nothing wrong, an earlier turn is
  // still being answered.
  info: { tone: 'info', icon: 'schedule' },
};

/**
 * Quiet inline status row: a chat-spawned background job's outcome
 * (author_long_document / equipment knowledge fetch), or a send the server
 * turned away because the session was still working (`info`). Visually
 * distinct from Bird's assistant bubbles: a faint chip carrying the message
 * and, for a created note, an "Open" affordance. Surfaced in the thread that
 * started it instead of only a transient toast.
 */
export function BirdStatusLine({ message }: BirdStatusLineProps) {
  const navigate = useNavigate();
  const { isMobile } = useMobileDetection();
  const { tone, icon } = KIND_STYLE[message.statusKind ?? 'success'];
  const noteRef = message.statusRef?.type === 'note' ? message.statusRef : null;

  const openNote = (): void => {
    if (!noteRef) return;
    navigate(isMobile ? `/m/notes/${noteRef.id}` : `/notes/${noteRef.id}`);
  };

  return (
    <Box mb={3} role="status" aria-live="polite" aria-atomic="true">
      <HStack
        align="flex-start"
        spacing={2}
        px={3}
        py={2}
        borderRadius="md"
        bg={`bg.${tone}`}
        borderWidth="1px"
        borderColor={`border.${tone}`}
      >
        <MaterialIcon name={icon} boxSize={4} color={`text.${tone}`} mt="2px" aria-hidden="true" />
        <Text fontSize="sm" color="text.secondary" flex="1" whiteSpace="pre-wrap">
          {message.content}
        </Text>
        {noteRef && (
          <Button size="xs" variant="outline" flexShrink={0} onClick={openNote}>
            Open
          </Button>
        )}
      </HStack>
    </Box>
  );
}
